import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Form, Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { Toaster, toast } from "react-hot-toast";

import KarigarLedgerPDF from "../components/KarigarLedgerPDF";
import { PDFViewer } from "@react-pdf/renderer";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import Pagination from "../components/Pagination";
import {
  listSuppliers,
  postKarigarLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";
import { extractListRowsFromResponse, parseApiPagination } from "../utils/parseApiPagination";

function KarigarLedger() {
  const { buttonClass } = useButtonTableStyle();
  const [selectedKarigar, setSelectedKarigar] = useState(null);
  const [karigarOptions, setKarigarOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const initialFetchRef = useRef(false);
  const ignoreNextPageFetchRef = useRef(false);
  const pageCacheRef = useRef(new Map());
  const karigarSearchRequestRef = useRef(0);

  const fetchKarigars = async (prefix = "") => {
    const trimmed = String(prefix || "").trim();
    const requestId = ++karigarSearchRequestRef.current;

    if (!trimmed || trimmed.length < 2) {
      setKarigarOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await listSuppliers({ search: trimmed, status: 1 });
      if (requestId !== karigarSearchRequestRef.current) return;

      let list = [];
      if (isErpApiSuccess(response)) {
        list = extractListRowsFromResponse(response, ["suppliers"]);
      }

      const options = list
        .filter((karigar) => {
          const status = String(karigar?.status ?? "").trim().toLowerCase();
          if (!status) return true;
          return ["1", "active", "enabled", "true"].includes(status);
        })
        .map((karigar) => {
          const displayName =
            karigar.company_name ||
            karigar.companyName ||
            karigar.name ||
            karigar.fullName ||
            karigar.supplier_name ||
            karigar.short_code ||
            (karigar.id != null ? `Supplier #${karigar.id}` : "");
          const contactNumber =
            karigar.contact_number ||
            karigar.contactNumber ||
            karigar.mobile ||
            karigar.phone ||
            "";

          return {
            value: karigar.id,
            label: contactNumber
              ? `${displayName} (${contactNumber})`
              : displayName,
            companyName: karigar.company_name || karigar.companyName || "",
            name: karigar.name || displayName,
            city: karigar.city || "",
          };
        })
        .filter((option) => option.label);

      setKarigarOptions(options);
    } catch (error) {
      if (requestId !== karigarSearchRequestRef.current) return;
      setKarigarOptions([]);
    } finally {
      if (requestId === karigarSearchRequestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;
  }, []);

  // Listen for API URL changes and clear report
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in KarigarLedger...', event.detail);
      setLedgerRows([]);
      setPdfData(null);
      setShowPDFModal(false);
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, []);

  const handleSubmit = async (
    e,
    { page = 1, limit = itemsPerPage, bypassCache = false } = {},
  ) => {
    if (e?.preventDefault) e.preventDefault();
    const refresh = Boolean(e) || bypassCache;
    const cacheKey = entireReport
      ? `${selectedKarigar?.value || ""}:${page}:${limit}`
      : "";
    if (refresh) pageCacheRef.current.clear();
    if (cacheKey && !refresh) {
      const cached = pageCacheRef.current.get(cacheKey);
      if (cached) {
        setLedgerRows(cached.ledgerRows);
        setPdfData(cached.pdfData);
        setCurrentPage(cached.currentPage);
        setTotalCount(cached.totalCount);
        setHasMorePages(cached.hasMorePages);
        setLoading(false);
        return;
      }
    }
    setLoading(true);

    const apiPayload = {
      kid: selectedKarigar.value,
    };

    if (entireReport) {
      apiPayload.type = "entire";
      apiPayload.page = page;
      apiPayload.limit = limit;
    } else {
      if (fromDate) apiPayload.from_date = fromDate;
      if (toDate) apiPayload.to_date = toDate;
    }

    try {
      const payload = await postKarigarLedger(apiPayload);

      if (isErpApiSuccess(payload) || payload?.message === "radha") {
        const entries = payload.data?.entries || [];

        if (!Array.isArray(entries) || entries.length === 0) {
          toast.error("No ledger entries found for this Karigar / date range.");
          return;
        }

        // Sort entries by date to ensure proper order (YYYY-MM-DD format)
        const sortedEntries = [...entries].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          // If dates are equal, maintain original order
          if (dateA.getTime() === dateB.getTime()) {
            return 0;
          }
          return dateA - dateB;
        });

        // Calculate opening balance from entries before the selected date range
        let openingGoldBalance = 0;
        let openingSilverBalance = 0;
        let openingAmountBalance = 0;
        let transactionsInPeriod = sortedEntries;

        if (!entireReport && fromDate) {
          const from = new Date(fromDate);
          from.setHours(0, 0, 0, 0);

          // Separate entries into opening balance (before fromDate) and period transactions
          const openingEntries = [];
          const periodEntries = [];

          sortedEntries.forEach((entry) => {
            const entryDate = new Date(entry.date);
            entryDate.setHours(0, 0, 0, 0);

            if (entryDate < from) {
              openingEntries.push(entry);
            } else if (!toDate || entryDate <= new Date(toDate)) {
              periodEntries.push(entry);
            }
          });

          // Calculate opening balance from entries before the period
          openingEntries.forEach((entry) => {
            openingGoldBalance +=
              (parseFloat(entry.gold_aavak) || 0) -
              (parseFloat(entry.gold_jaavak) || 0);
            openingSilverBalance +=
              (parseFloat(entry.silver_aavak) || 0) -
              (parseFloat(entry.silver_jaavak) || 0);
            openingAmountBalance +=
              (parseFloat(entry.aavak_amt) || 0) -
              (parseFloat(entry.jaavak_amt) || 0);
          });

          transactionsInPeriod = periodEntries;
        } else {
          // For entire report, calculate opening balance from all entries before the first entry
          // (Opening balance starts at 0 for entire report)
          openingGoldBalance = 0;
          openingSilverBalance = 0;
          openingAmountBalance = 0;
        }

        // const openingBalanceRow = {
        //   date: "-",
        //   remarks: "Opening Balance",
        //   isOpening: true,
        //   type: "Opening Balance",
        //   gwt: "",
        //   lwt: "",
        //   credit: "",
        //   debit: "",
        //   balance: openingGoldBalance.toFixed(3),
        //   silver_gwt: "",
        //   silver_lwt: "",
        //   silver_credit: "",
        //   silver_debit: "",
        //   silver_balance: openingSilverBalance.toFixed(3),
        //   amount_credit: "",
        //   amount_debit: "",
        //   amount_balance: openingAmountBalance.toFixed(2),
        // };

        let goldBalance = openingGoldBalance;
        let silverBalance = openingSilverBalance;
        let amountBalance = openingAmountBalance;

        const ledgerData = transactionsInPeriod.map((entry) => {
          const gold_credit = parseFloat(entry.gold_aavak) || 0;
          const gold_debit = parseFloat(entry.gold_jaavak) || 0;
          goldBalance += gold_credit - gold_debit;

          const silver_credit = parseFloat(entry.silver_aavak) || 0;
          const silver_debit = parseFloat(entry.silver_jaavak) || 0;
          silverBalance += silver_credit - silver_debit;

          const amount_credit = parseFloat(entry.aavak_amt) || 0;
          const amount_debit = parseFloat(entry.jaavak_amt) || 0;
          amountBalance += amount_credit - amount_debit;

          return {
            date: entry.date || "-",
            gwt: "",
            lwt: "",
            credit: gold_credit ? gold_credit.toFixed(3) : "",
            debit: gold_debit ? gold_debit.toFixed(3) : "",
            balance: goldBalance.toFixed(3),
            silver_gwt: "",
            silver_lwt: "",
            silver_credit: silver_credit ? silver_credit.toFixed(3) : "",
            silver_debit: silver_debit ? silver_debit.toFixed(3) : "",
            silver_balance: silverBalance.toFixed(3),
            amount_credit: amount_credit ? amount_credit.toFixed(2) : "",
            amount_debit: amount_debit ? amount_debit.toFixed(2) : "",
            amount_balance: amountBalance.toFixed(2),
            type: entry.type || "-",
          };
        });

        const finalLedgerData = [...ledgerData];

        // Save for table view
        setLedgerRows(finalLedgerData);

        const paginationMeta = entireReport
          ? parseApiPagination(payload, finalLedgerData.length)
          : null;

        if (entireReport && paginationMeta) {
          setCurrentPage(paginationMeta.page);
          setTotalCount(paginationMeta.total);
          setHasMorePages(paginationMeta.hasMore);
          if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
        } else {
          setCurrentPage(1);
          setTotalCount(finalLedgerData.length);
          setHasMorePages(false);
        }

        // Also prepare data for PDF (used when user clicks Print)
        const nextPdf = {
          ledgerData: finalLedgerData,
          karigarInfo: selectedKarigar,
          fromDate,
          toDate,
          entireReport,
        };
        setPdfData(nextPdf);
        if (cacheKey && paginationMeta) {
          pageCacheRef.current.set(cacheKey, {
            ledgerRows: finalLedgerData,
            pdfData: nextPdf,
            currentPage: paginationMeta.page,
            totalCount: paginationMeta.total,
            hasMorePages: paginationMeta.hasMore,
          });
        }
        toast.success("Ledger generated successfully!");
      } else {
        toast.error("Failed to fetch ledger data. Please try again.");
        console.error("Failed to fetch ledger data:", payload);
      }
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while fetching the ledger data."
        )
      );
      console.error("Error fetching karigar ledger:", error);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!pdfData || !ledgerRows.length) {
      toast.error("Please generate the ledger first.");
      return;
    }
    setShowPDFModal(true);
  };

  // Memoize the PDF component to avoid unnecessary rerenders
  const pdfViewer = useMemo(() => {
    if (!pdfData) return null;
    return (
      <PDFViewer width="100%" height="100%">
        <KarigarLedgerPDF
          ledgerData={pdfData.ledgerData}
          karigarInfo={pdfData.karigarInfo}
          fromDate={pdfData.fromDate}
          toDate={pdfData.toDate}
          entireReport={pdfData.entireReport}
        />
      </PDFViewer>
    );
  }, [pdfData]);

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card">
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Supplier/Karigar Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* Karigar Dropdown */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">Select Party</label>
                  <Select
                    options={karigarOptions}
                    value={selectedKarigar}
                    onChange={setSelectedKarigar}
                    onInputChange={(val, { action }) => {
                      if (action === "input-change") {
                        fetchKarigars(val);
                      }
                      return val;
                    }}
                    filterOption={() => true}
                    openMenuOnFocus
                    openMenuOnClick
                    noOptionsMessage={() =>
                      loading ? "Searching..." : "No matching parties"
                    }
                    placeholder={
                      loading ? "Loading..." : "Search and select Party..."
                    }
                    isClearable
                    isSearchable
                    isLoading={loading}
                  />
                </div>
                {/* From Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={!selectedKarigar}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Ledger -{" "}
                    {selectedKarigar?.label ? selectedKarigar.label : ""}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                </div>
                <div className="table-responsive mb-4 border rounded-1">
                  <table className="table text-nowrap mb-0 align-middle">
                    <thead className="text-dark fs-4">
                      <tr>
                        <th colSpan="2" className="text-center"></th>
                        <th colSpan="3" className="text-center text-success">
                          Credited
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                          style={{ color: "#dc3545" }}
                        >
                          Debited
                        </th>
                        <th colSpan="3" className="text-center">
                          Balance
                        </th>
                      </tr>
                      <tr>
                        <th className="text-nowrap">Date</th>
                        <th className="text-nowrap">Tag No</th>
                        <th className="text-end text-nowrap text-success">
                          Gold
                        </th>
                        <th className="text-end text-nowrap text-success">
                          Silver
                        </th>
                        <th className="text-end text-nowrap text-success">
                          Amount
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Gold
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Silver
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Amount
                        </th>
                        <th className="text-end text-nowrap">Gold</th>
                        <th className="text-end text-nowrap">Silver</th>
                        <th className="text-end text-nowrap">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((row, idx) => {
                        const cellStyle = row.isOpening
                          ? { backgroundColor: "#E3F2FD", fontWeight: 500 }
                          : undefined;
                        // Extract only date part (YYYY-MM-DD) from date string
                        const dateValue = row.isOpening
                          ? "-"
                          : row.date
                            ? row.date.split(" ")[0]
                            : "-";
                        return (
                          <tr key={idx}>
                            <td style={cellStyle}>{dateValue}</td>
                            <td style={cellStyle}>{row.type || "-"}</td>
                            <td
                              className={`text-end ${row.credit ? "text-success" : ""
                                }`}
                              style={cellStyle}
                            >
                              {row.credit || "-"}
                            </td>
                            <td
                              className={`text-end ${row.silver_credit ? "text-success" : ""
                                }`}
                              style={cellStyle}
                            >
                              {row.silver_credit || "-"}
                            </td>
                            <td
                              className={`text-end ${row.amount_credit ? "text-success" : ""
                                }`}
                              style={cellStyle}
                            >
                              {row.amount_credit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.silver_debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.silver_debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.amount_debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.amount_debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.balance || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.silver_balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.silver_balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.silver_balance || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.amount_balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.amount_balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.amount_balance || "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {entireReport && totalCount > 0 && (
                  <Pagination
                    totalItems={totalCount}
                    hasMore={hasMorePages}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={(page) => {
                      if (ignoreNextPageFetchRef.current) {
                        ignoreNextPageFetchRef.current = false;
                        return;
                      }
                      handleSubmit(undefined, { page, limit: itemsPerPage });
                    }}
                    onItemsPerPageChange={(limit) => {
                      ignoreNextPageFetchRef.current = true;
                      setItemsPerPage(limit);
                      setCurrentPage(1);
                      handleSubmit(undefined, {
                        page: 1,
                        limit,
                        bypassCache: true,
                      });
                    }}
                    itemsPerPageOptions={[10, 20, 30, 50, 100]}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => setShowPDFModal(false)}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Supplier/Karigar Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {showPDFModal && pdfData && pdfViewer}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default KarigarLedger;
